// ─── Table variant style system ────────────────────────────────────────────────
//
// Background and structural classes are intentionally separated so that
// per-row and per-column bg overrides can slot in without fighting Tailwind's
// class-merge behaviour. Renderers apply bg separately from structural classes.

import type { TableBg } from '../api/types/article-blocks'

export type { TableBg }

export type TableVariant =
  | 'default'
  | 'banded'
  | 'banded-colored'
  | 'financial'
  | 'rules'
  | 'banded-column'
  | 'banded-column-colored'

export const TABLE_VARIANT_LABELS: Record<TableVariant, string> = {
  'default':               'Default',
  'banded':                'Banded',
  'banded-colored':        'Banded (Color)',
  'financial':             'Financial',
  'rules':                 'Rules',
  'banded-column':         'Column Banded',
  'banded-column-colored': 'Column Banded (Color)',
}

// ─── Named background palette ─────────────────────────────────────────────────

export const TABLE_BG_LABELS: Record<TableBg, string> = {
  yellow: 'Yellow',
  blue:   'Blue',
  red:    'Red',
  green:  'Green',
  violet: 'Violet',
  amber:  'Amber',
}

/** Tailwind bg class for each named colour at a visible-but-not-overwhelming intensity. */
export const TABLE_BG_CLASSES: Record<TableBg, string> = {
  yellow: 'bg-yellow-3',
  blue:   'bg-[var(--blue-4)]',
  red:    'bg-[var(--red-4)]',
  green:  'bg-[var(--jade-4)]',
  violet: 'bg-[var(--violet-4)]',
  amber:  'bg-[var(--amber-4)]',
}

/** Dot colour for the swatch UI (step 9 = solid, readable on any surface). */
export const TABLE_BG_DOT_CLASSES: Record<TableBg, string> = {
  yellow: 'bg-yellow-9',
  blue:   'bg-[var(--blue-9)]',
  red:    'bg-[var(--red-9)]',
  green:  'bg-[var(--jade-9)]',
  violet: 'bg-[var(--violet-9)]',
  amber:  'bg-[var(--amber-9)]',
}

// ─── Variant style interface ──────────────────────────────────────────────────

export interface TableVariantStyles {
  /** Classes on <table> */
  table: string
  /** Classes on each header <tr> */
  headerTr: string
  /** Classes on each header <th> — receives column index */
  headerTh: (ci: number) => string
  /** Structural (non-bg) classes for each body <tr> */
  bodyTrBase: (ri: number) => string
  /**
   * Variant row bg — only for row-stripe variants.
   * Returns '' for column-stripe and no-stripe variants.
   * Renderers MUST ignore this when an explicit row override is present.
   */
  bodyRowBg: (ri: number) => string
  /**
   * Variant cell bg — only for column-stripe variants.
   * Returns '' for row-stripe and no-stripe variants.
   * Renderers MUST ignore this when any row-level bg is present.
   */
  bodyCellBg: (ci: number) => string
  /** Non-bg classes for body <td> cells (padding, text colour) */
  bodyTdBase: () => string
  /** Non-bg classes for the row-header <th> */
  bodyRowHeaderThBase: () => string
  /** Default bg for the row-header <th>. Overridden when the row has an explicit bg. */
  bodyRowHeaderBg: () => string
}

// ─── Shared constants ─────────────────────────────────────────────────────────

const PAD       = 'px-4 py-2.5'
const PAD_TIGHT = 'px-3 py-1.5'
const HEADER_LABEL         = 'text-left text-xs font-bold uppercase tracking-wide text-fg-secondary'
const HEADER_LABEL_COLORED = 'text-left text-xs font-bold uppercase tracking-wide text-accent-tertiary-fg'

// ─── Variant implementations ──────────────────────────────────────────────────

export function getTableVariantStyles(variant: TableVariant = 'default'): TableVariantStyles {
  switch (variant) {

    case 'banded':
      return {
        table:               'w-full',
        headerTr:            'border-b-2 border-line-strong bg-fill-active',
        headerTh:            () => `${PAD} ${HEADER_LABEL}`,
        bodyTrBase:          () => '',
        bodyRowBg:           (ri) => ri % 2 !== 0 ? 'bg-fill' : '',
        bodyCellBg:          () => '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }

    case 'banded-colored':
      return {
        table:               'w-full',
        headerTr:            'border-b-2 border-line-strong bg-fill-active',
        headerTh:            () => `${PAD} ${HEADER_LABEL}`,
        bodyTrBase:          () => '',
        bodyRowBg:           (ri) => ri % 2 !== 0 ? 'bg-[var(--violet-4)]' : '',
        bodyCellBg:          () => '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }

    case 'financial':
      return {
        table:               'w-full border-t-2 border-line-strong',
        headerTr:            'border-b-2 border-line-strong bg-fill-active',
        headerTh:            () => `${PAD_TIGHT} ${HEADER_LABEL}`,
        bodyTrBase:          () => 'border-b border-line-subtle last:border-b-2 last:border-line-strong',
        bodyRowBg:           () => '',
        bodyCellBg:          () => '',
        bodyTdBase:          () => `${PAD_TIGHT} tabular-nums text-fg`,
        bodyRowHeaderThBase: () => `${PAD_TIGHT} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }

    case 'rules':
      return {
        table:               'w-full',
        headerTr:            'border-b-2 border-line-strong',
        headerTh:            () => `${PAD} ${HEADER_LABEL}`,
        bodyTrBase:          () => 'border-b border-line-subtle last:border-0',
        bodyRowBg:           () => '',
        bodyCellBg:          () => '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => '',
      }

    case 'banded-column':
      return {
        table:               'w-full',
        headerTr:            'border-b border-line bg-fill-active',
        headerTh:            (ci) => `${PAD} ${HEADER_LABEL}${ci % 2 !== 0 ? ' bg-fill' : ''}`,
        bodyTrBase:          () => 'border-b border-line-subtle last:border-0',
        bodyRowBg:           () => '',
        bodyCellBg:          (ci) => ci % 2 !== 0 ? 'bg-fill' : '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }

    case 'banded-column-colored':
      return {
        table:               'w-full',
        headerTr:            'border-b border-line bg-fill-active',
        headerTh:            (ci) =>
          ci % 2 !== 0
            ? `${PAD} ${HEADER_LABEL_COLORED} bg-accent-tertiary-fill`
            : `${PAD} ${HEADER_LABEL}`,
        bodyTrBase:          () => 'border-b border-line-subtle last:border-0',
        bodyRowBg:           () => '',
        bodyCellBg:          (ci) => ci % 2 !== 0 ? 'bg-[var(--violet-4)]' : '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }

    case 'default':
    default:
      return {
        table:               'w-full',
        headerTr:            'border-b border-line bg-fill-active',
        headerTh:            () => `${PAD} ${HEADER_LABEL}`,
        bodyTrBase:          () => 'border-b border-line last:border-0',
        bodyRowBg:           (ri) => ri % 2 !== 0 ? 'bg-fill' : '',
        bodyCellBg:          () => '',
        bodyTdBase:          () => `${PAD} text-fg`,
        bodyRowHeaderThBase: () => `${PAD} ${HEADER_LABEL}`,
        bodyRowHeaderBg:     () => 'bg-fill-active',
      }
  }
}
